import React, { useCallback, useState, useRef } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import ChannelSelector from './channels/ChannelSelector'
import { cloneDeepGridEditorGroup, cloneDeepGridEditorChannel } from '../../../../cloneDeepFunctions'
import GroupTitleConfig from './configs/GroupTitleConfig'
import SelectedChannel from './channels/SelectedChannel'
import { AxesSuffix } from 'live/components/widget/ui/y-axis-config/y-axis-config-modal'
import NumericField from '../../NumericField'
import Toggle from '../../Toggle'
import theme from 'live/services/theme'
import ColorPicker from 'live/components/ui/form/color-picker'

interface Props {
    group: GridEditorGroup
    onChangeGroup: (newGroup: GridEditorGroup, updateQuery: boolean) => void
    onRemoveGroup: (groupId: number, updateQuery: boolean) => void
    widgetConfig?: GridConfig
    canDeleteGroup: boolean
    loadingChannels: boolean
    curves: Array<AssetCurves>
    isEditor: boolean
    channelsLiteral?: AllChannelsMap
    getChannelLockState?: (opt: LiveSelectChannelOption) => boolean
    onLock?: (channelOption: LiveSelectChannelOption, lock: boolean) => void
    syncLockedChannelsConfig?: (option, value, field) => void
    isTableMode?: boolean
}

export default function GroupConfig(props: Props): JSX.Element {
    const group: GridEditorGroup = cloneDeepGridEditorGroup(props?.group)

    const { onChangeGroup, onRemoveGroup, canDeleteGroup, loadingChannels } = props

    const [groupTitle, setGroupTitle] = useState(group.title || group.defaultTitle)

    const reference = useRef(null)
    const [open, setOpen] = useState(group.isOpen)
    const toggleOpen = (): void => {
        reference.current.classList.remove('selectedGroupOpen')
        changeGroupConfig('isOpen', !open)
        setOpen(s => !s)
    }

    const changeGroupChannels = (newChannels: Array<GridEditorChannelOption>, updateQuery: boolean): void => {
        onChangeGroup(
            {
                ...group,
                channels: newChannels?.map(channel => cloneDeepGridEditorChannel(channel))
            },
            updateQuery
        )
    }

    const changeGroupConfig = (configKey: string, newValue: boolean | number | string): void => {
        const newGroup: GridEditorGroup = cloneDeepGridEditorGroup(group)
        newGroup[configKey] = newValue
        if (configKey === 'title') {
            setGroupTitle(newValue.toString())
        }
        onChangeGroup(newGroup, false)
    }

    const renderEntireSelectedOption = useCallback(
        ({ option, onRemove }: { option: LiveSelectChannelOption; onRemove: () => void }) => {
            if (!option.value) return null // didn't find defaultChannel  - probably renamed or deleted

            const changeGroupChannelConfig = (
                mnemonic: string,
                configKey: string,
                newValue: string | boolean | AlarmsProps
            ): void => {
                const newGroup: GridEditorGroup = cloneDeepGridEditorGroup(group)
                newGroup?.channels?.map(channel => {
                    if (channel?.mnemonic === mnemonic) {
                        channel[configKey] = newValue
                    }

                    return channel
                })
                onChangeGroup(newGroup, false)
            }

            return (
                <SelectedChannel
                    key={option.value}
                    channelsLiteral={props.channelsLiteral}
                    onRemove={onRemove}
                    onChangeGroupChannelConfig={changeGroupChannelConfig}
                    channelOption={option}
                    onLock={props.onLock}
                    getChannelLockState={props.getChannelLockState}
                    syncLockedChannelsConfig={props.syncLockedChannelsConfig}
                    widgetConfig={props?.widgetConfig}
                    onChangeGroup={onChangeGroup}
                />
            )
        },
        [
            props.channelsLiteral,
            props.onLock,
            props.getChannelLockState,
            props.syncLockedChannelsConfig,
            props?.widgetConfig,
            group,
            onChangeGroup
        ]
    )

    return (
        <GroupConfigContainer
            isEditor={props.isEditor}
            data-testid={'group-config-' + group.id}
            ref={reference}
            className={'group-config-container'}
            style={{ marginBottom: open ? '10px' : '5px' }}
        >
            <header style={{ padding: '0' }}>
                <span
                    onClick={toggleOpen}
                    style={{
                        width: '100%',
                        cursor: 'pointer',
                        padding: `9px ${canDeleteGroup ? '7px' : '11px'} 9px 11px`
                    }}
                >
                    {groupTitle || group.defaultTitle}
                    <span style={{ float: 'right' }}>
                        <i
                            role="group-config-toggler"
                            className={`fas color-primary fa-angle-${open ? 'up' : 'down'} toggler-icon`}
                        />
                    </span>
                </span>

                {canDeleteGroup && (
                    <i
                        title={i18n('Remove') + ' ' + group.title}
                        style={{ alignSelf: 'center', padding: '9px 11px 9px 0' }}
                        onClick={() => onRemoveGroup(group.id, group.channels?.length > 0)}
                        className="far fa-times-circle"
                    />
                )}
            </header>
            <div className={`${open ? 'openGroupBox' : 'closedGroupBox'}`}>
                <div className="channel-selector-container">
                    {<label>{i18n('Channels')}</label>}
                    <ChannelSelector
                        group={cloneDeepGridEditorGroup(group)}
                        channels={props.curves}
                        selectedChannels={group?.channels?.map(channel => cloneDeepGridEditorChannel(channel))}
                        onChangeGroupChannels={changeGroupChannels}
                        renderEntireSelectedOption={renderEntireSelectedOption}
                        loadingGroups={true}
                        loadingChannels={loadingChannels}
                    />
                </div>

                <div className="group-configs-container">
                    <ColorConfigContainer>
                        <ColorPicker
                            value={group.groupColor}
                            onChange={value => changeGroupConfig('groupColor', value)}
                            size={20}
                            zIndex={21}
                            caretColor={theme.gray}
                            resetColor={''}
                            position="left"
                        />

                        <label style={{ marginLeft: '8px', marginTop: 'auto' }}>{`${i18n('Group color')}`}</label>
                    </ColorConfigContainer>
                    <GroupTitleConfig
                        label={i18n('Group title')}
                        value={group.title}
                        configKey={'title'}
                        onChange={changeGroupConfig}
                        handleTitleChange={(newValue: string) => setGroupTitle(newValue || group.defaultTitle)}
                        className={!props.isEditor ? 'dropdown-toggler--cancel-drag' : ''}
                    />

                    {!props.isTableMode && (
                        <>
                            <NumericField
                                label={i18n('Number of columns')}
                                value={group.nColumns}
                                configKey={'nColumns'}
                                onChange={changeGroupConfig}
                                minValueAllowed={1}
                                className={!props.isEditor ? 'dropdown-toggler--cancel-drag' : ''}
                            />
                        </>
                    )}

                    <NumericField
                        label={i18n('Group Title font size')}
                        value={group.groupTitleFontSize}
                        configKey={'groupTitleFontSize'}
                        onChange={changeGroupConfig}
                        minValueAllowed={1}
                        className={!props.isEditor ? 'dropdown-toggler--cancel-drag' : ''}
                    >
                        <AxesSuffix>{'px'}</AxesSuffix>
                    </NumericField>
                    <NumericField
                        label={i18n('Title font size')}
                        value={group.titleFontSize}
                        configKey={'titleFontSize'}
                        onChange={changeGroupConfig}
                        minValueAllowed={1}
                        className={!props.isEditor ? 'dropdown-toggler--cancel-drag' : ''}
                    >
                        <AxesSuffix>{'px'}</AxesSuffix>
                    </NumericField>
                    <NumericField
                        label={i18n('Value font size')}
                        value={group.valueFontSize}
                        configKey={'valueFontSize'}
                        onChange={changeGroupConfig}
                        minValueAllowed={1}
                        className={!props.isEditor ? 'dropdown-toggler--cancel-drag' : ''}
                    >
                        <AxesSuffix>{'px'}</AxesSuffix>
                    </NumericField>
                    <NumericField
                        label={i18n('Uom font size')}
                        value={group.uomFontSize}
                        configKey={'uomFontSize'}
                        onChange={changeGroupConfig}
                        minValueAllowed={1}
                        className={!props.isEditor ? 'dropdown-toggler--cancel-drag' : ''}
                    >
                        <AxesSuffix>{'px'}</AxesSuffix>
                    </NumericField>

                    <Toggle
                        label={i18n('Title bold')}
                        value={group.titleBold}
                        configKey={'titleBold'}
                        onChange={changeGroupConfig}
                    />
                    <Toggle
                        label={i18n('Value bold')}
                        value={group.valueBold}
                        configKey={'valueBold'}
                        onChange={changeGroupConfig}
                    />
                    <Toggle
                        label={i18n('Uom bold')}
                        value={group.uomBold}
                        configKey={'uomBold'}
                        onChange={changeGroupConfig}
                    />
                </div>
            </div>
        </GroupConfigContainer>
    )
}

const ColorConfigContainer = styled.div`
    padding-top: 4px;
    margin-bottom: 10px;
    display: flex;
`

export const GroupConfigContainer = styled.div`
    border-radius: 3px;
    .toggler-icon {
        color: ${({ theme }) => theme['link-color']};
        &:hover {
            color: ${({ theme }) => theme['link-color']};
        }
    }
    .closedGroupBox {
        overflow: hidden;
        padding: 0px;
        height: 0px;
    }

    .openGroupBox {
        overflow: visible;
        padding: 0px;
        border-top: 1px solid
            ${({ theme }): string => (theme['black-scale'] ? theme['darker-bg'] : theme['gray-lighter'])};
    }

    .channel-selector-container {
        padding: 10px 11px 0 11px;
    }

    .option-list__item-container {
        margin-bottom: 10px;
    }

    .group-configs-container {
        padding: 0px 11px 9px 11px;
    }
    > header {
        padding: 9px 11px;
        background: ${({ isEditor }) => (isEditor ? 'var(--gray-300)' : 'var(--static-gray-900)')};
        color: ${({ theme }): string => theme['main-typography']};
        font-family: ${({ theme }): string => theme['font-semibold']};
        font-size: 14px;
        display: flex;
        justify-content: space-between;

        i {
            cursor: pointer;

            &:hover {
                color: ${({ theme }): string => theme['brand-danger']};
            }
        }
    }

    > div {
        padding: 5px 11px;
    }

    ${ChannelSelector} {
        form {
            margin-bottom: 5px;
        }
    }
`
